import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tickets } from '@repletoryreach/sdk-api';
import { ActionIcon, Badge, Button, Divider, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { Checkbox, NumberInput, Select, Switch, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import type { Indexed } from '../../../lib/types';
import { ColourPicker } from '../../bookings/components/ColourPicker';
import { SWATCHES } from '../../bookings/lib/data';
import { TypeTag } from '../components/TicketBits';
import { TICKETS_KEY, ticketErrorText, useLookups, useTicketTypes, type Lookups } from '../lib/data';
import { fieldKey, parseOptions } from '../lib/fields';
import { moveItem } from '../lib/rules';

const MUTED = 'var(--rr-color-text-muted)';
const MAX_FIELDS = 30;

interface FieldDraft extends tickets.TypeField {
  /** Saved fields keep their key: values already stored live under it. */
  saved: boolean;
  optionsText: string;
}

interface Draft {
  name: string;
  description: string;
  colour: string | null;
  default_team_id: string;
  default_priority_id: string;
  sort_order: number | '';
  active: boolean;
  fields: FieldDraft[];
}

function toDraft(x: tickets.TicketType | null): Draft {
  return {
    name: x?.name ?? '',
    description: x?.description ?? '',
    colour: x?.colour ?? SWATCHES[0],
    default_team_id: x?.default_team_id ?? '',
    default_priority_id: x?.default_priority_id ?? '',
    sort_order: x?.sort_order ?? 0,
    active: x?.active ?? true,
    fields: (x?.fields ?? []).map((f) => ({ ...f, saved: true, optionsText: (f.options ?? []).join(', ') }))
  };
}

function problemOf(d: Draft): string | null {
  if (!d.name.trim()) return 'tickets.types.error.name';
  for (const f of d.fields) {
    if (!f.label.trim()) return 'tickets.types.error.fieldLabel';
    if ((f.type === 'select' || f.type === 'multi_select') && parseOptions(f.optionsText).length < 2) return 'tickets.types.error.options';
  }
  return null;
}

export function TypesSection({ canEdit, limit }: { canEdit: boolean; limit: number | null | undefined }) {
  const t = useT();
  const qc = useQueryClient();
  const q = useTicketTypes();
  const lk = useLookups();
  const [editing, setEditing] = useState<tickets.TicketType | 'new' | null>(null);
  const rows = [...(q.data?.data ?? [])].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)) as Indexed<tickets.TicketType>[];
  const activeCount = rows.filter((r) => r.active).length;
  const atLimit = limit != null && rows.length >= limit;

  return (
    <Stack gap="md">
      <TableShell
        data-guide="tickets-types"
        title={t('tickets.types.title')}
        description={limit == null ? t('tickets.types.intro') : t('tickets.types.introLimit', { used: rows.length, limit })}
        actions={canEdit ? (
          <Button size="sm" data-guide="tickets-types-add" disabled={atLimit} title={atLimit ? t('tickets.error.PLAN_LIMIT_REACHED') : undefined} onClick={() => setEditing('new')}>
            {t('tickets.types.add')}
          </Button>
        ) : undefined}
        loading={q.isPending}
        error={q.error ? (
          <Alert color="error" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{ticketErrorText(t, q.error)}</span>
              <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : undefined}
        isEmpty={!q.isPending && rows.length === 0}
        empty={<EmptyState title={t('tickets.types.empty')} description={t('tickets.types.emptyHint')} />}
      >
        <DataTable<Indexed<tickets.TicketType>>
          records={rows}
          idAccessor="id"
          onRowClick={(r) => setEditing(r)}
          columns={[
            { accessor: 'name', title: t('common.name'), render: (r) => <TypeTag type={r} /> },
            { accessor: 'fields', title: t('tickets.types.fields'), render: (r) => <Text role="bodySmall">{r.fields.length ? r.fields.map((f) => f.label).join(', ') : t('common.none')}</Text> },
            { accessor: 'default_team_id', title: t('tickets.types.defaultTeam'), render: (r) => <Text role="bodySmall">{lk.teamName(r.default_team_id) || t('common.none')}</Text> },
            { accessor: 'default_priority_id', title: t('tickets.types.defaultPriority'), render: (r) => <Text role="bodySmall">{(r.default_priority_id && lk.priorityById.get(r.default_priority_id)?.name) || t('common.none')}</Text> },
            { accessor: 'active', title: t('common.status'), render: (r) => <Badge size="sm" variant="light" color={r.active ? 'green' : 'gray'}>{r.active ? t('tickets.active') : t('tickets.inactive')}</Badge> }
          ]}
        />
      </TableShell>
      {limit != null && <Text role="caption" c={MUTED}>{t('tickets.types.limitHint', { active: activeCount })}</Text>}

      <TypeDrawer
        target={editing}
        lk={lk}
        canEdit={canEdit}
        onClose={() => setEditing(null)}
        onSaved={() => void qc.invalidateQueries({ queryKey: [TICKETS_KEY] })}
      />
    </Stack>
  );
}

function TypeDrawer({ target, lk, canEdit, onClose, onSaved }: {
  target: tickets.TicketType | 'new' | null;
  lk: Lookups;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const existing = target && target !== 'new' ? target : null;
  const open = target !== null;
  const [d, setD] = useState<Draft>(() => toDraft(null));
  const [initial, setInitial] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const draft = toDraft(existing);
    setD(draft);
    setInitial(JSON.stringify(draft));
    setTried(false);
    setError(null);
  }, [open, existing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const dirty = open && JSON.stringify(d) !== initial;
  const problem = problemOf(d);
  const ro = !canEdit;

  function requestClose() {
    if (!dirty || ro) { onClose(); return; }
    openConfirmModal({
      title: t('tickets.discard.title'),
      children: <Text role="bodySmall">{t('tickets.discard.body')}</Text>,
      labels: { confirm: t('action.discard'), cancel: t('action.keep') },
      confirmProps: { color: 'red' },
      onConfirm: onClose
    });
  }

  function setField(i: number, patch: Partial<FieldDraft>) {
    setD((x) => ({
      ...x,
      fields: x.fields.map((f, j) => {
        if (j !== i) return f;
        const next = { ...f, ...patch };
        if (patch.label !== undefined && !f.saved) next.key = fieldKey(patch.label, x.fields.filter((_, k) => k !== i).map((o) => o.key));
        return next;
      })
    }));
  }

  function addField() {
    setD((x) => ({
      ...x,
      fields: [...x.fields, { key: fieldKey('', x.fields.map((f) => f.key)), label: '', type: 'text', required: false, client_visible: false, saved: false, optionsText: '' }]
    }));
  }

  async function save() {
    setTried(true);
    if (problem) return;
    setBusy(true);
    setError(null);
    const body: Partial<tickets.TicketTypeInput> & { name: string } = {
      name: d.name.trim(),
      description: d.description.trim() || null,
      colour: d.colour,
      default_team_id: d.default_team_id || null,
      default_priority_id: d.default_priority_id || null,
      sort_order: d.sort_order === '' ? 0 : d.sort_order,
      active: d.active,
      fields: d.fields.map((f) => ({
        key: f.key,
        label: f.label.trim(),
        type: f.type,
        required: f.required,
        client_visible: f.client_visible,
        ...(f.type === 'select' || f.type === 'multi_select' ? { options: parseOptions(f.optionsText) } : {})
      }))
    };
    try {
      const res = existing ? await tickets.updateType(existing.id, body) : await tickets.createType(body);
      notifySuccess(t(existing ? 'tickets.types.toast.saved' : 'tickets.types.toast.created', { name: res.data.name }), t('tickets.types.title'));
      onSaved();
      onClose();
    } catch (e) {
      setError(ticketErrorText(t, e));
    } finally {
      setBusy(false);
    }
  }

  function remove() {
    if (!existing) return;
    openConfirmModal({
      title: t('tickets.types.delete.title'),
      children: <Text role="bodySmall">{t('tickets.types.delete.body', { name: existing.name })}</Text>,
      labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await tickets.deleteType(existing.id);
          notifySuccess(t('tickets.types.toast.deleted', { name: existing.name }), t('tickets.types.title'));
          onSaved();
          onClose();
        } catch (e) {
          notifyError(ticketErrorText(t, e), t('tickets.types.title'));
        }
      }
    });
  }

  return (
    <Drawer opened={open} onClose={requestClose} title={existing ? existing.name : t('tickets.types.new')} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); if (canEdit) void save(); }}>
        <Stack gap="md" data-testid="type-editor">
          {ro && <Alert color="info">{t('tickets.readOnly')}</Alert>}
          <div data-guide="type-name">
            <TextInput label={t('common.name')} value={d.name} onChange={(e) => set({ name: e.currentTarget.value })} required maxLength={80} disabled={ro}
              error={tried && problem === 'tickets.types.error.name' ? t(problem) : undefined} />
          </div>
          <Textarea label={t('common.description')} value={d.description} onChange={(e) => set({ description: e.currentTarget.value })} minRows={2} maxLength={500} disabled={ro}
            description={t('tickets.types.descriptionHint')} />
          <ColourPicker label={t('tickets.types.colour')} value={d.colour} onChange={(c) => set({ colour: c })} disabled={ro} />
          <Group gap="sm" grow wrap="wrap" align="flex-start" data-guide="type-defaults">
            <Select label={t('tickets.types.defaultTeam')} value={d.default_team_id} onChange={(e) => set({ default_team_id: e.currentTarget.value })} disabled={ro}
              data={[{ value: '', label: t('tickets.noTeam') }, ...lk.teams.map((x) => ({ value: x.id, label: x.name }))]}
              description={t('tickets.types.defaultTeamHint')} />
            <Select label={t('tickets.types.defaultPriority')} value={d.default_priority_id} onChange={(e) => set({ default_priority_id: e.currentTarget.value })} disabled={ro}
              data={[{ value: '', label: t('tickets.types.orgDefault') }, ...lk.priorities.map((p) => ({ value: p.id, label: p.name }))]} />
          </Group>

          <Divider />

          <Stack gap="sm" data-testid="field-builder" data-guide="type-fields">
            <Stack gap={2}>
              <Text role="inputLabel">{t('tickets.types.fields')}</Text>
              <Text role="caption" c={MUTED}>{t('tickets.types.fieldsHint')}</Text>
            </Stack>
            {d.fields.length === 0 && <Text role="bodySmall" c={MUTED}>{t('tickets.types.noFields')}</Text>}
            {d.fields.map((f, i) => (
              <Stack key={i} gap="xs" className="rr-tickets__row">
                <Group gap="xs" align="flex-end" wrap="wrap">
                  <TextInput label={t('tickets.types.fieldLabel')} value={f.label} onChange={(e) => setField(i, { label: e.currentTarget.value })} maxLength={80} disabled={ro}
                    style={{ flex: '1 1 200px' }} required />
                  <Select label={t('common.type')} value={f.type} disabled={ro}
                    onChange={(e) => setField(i, { type: e.currentTarget.value as tickets.FieldType })}
                    data={tickets.FIELD_TYPES.map((ty) => ({ value: ty, label: t(`tickets.fieldType.${ty}`) }))} />
                </Group>
                {(f.type === 'select' || f.type === 'multi_select') && (
                  <TextInput label={t('tickets.types.options')} value={f.optionsText} onChange={(e) => setField(i, { optionsText: e.currentTarget.value })} disabled={ro}
                    description={t('tickets.types.optionsHint')} />
                )}
                <Group gap="sm" justify="space-between" wrap="wrap">
                  <Group gap="md" wrap="wrap">
                    <Checkbox label={t('common.required')} checked={f.required} onChange={(on) => setField(i, { required: on })} disabled={ro} />
                    <Checkbox label={t('tickets.types.clientVisible')} checked={f.client_visible} onChange={(on) => setField(i, { client_visible: on })} disabled={ro} />
                  </Group>
                  <Group gap="xs">
                    <Text role="caption" c={MUTED}>{t('tickets.types.fieldKey', { key: f.key })}</Text>
                    {canEdit && (
                      <>
                        <ActionIcon size="sm" variant="subtle" aria-label={t('tickets.moveUp')} disabled={i === 0}
                          onClick={() => set({ fields: moveItem(d.fields, i, i - 1) })}>↑</ActionIcon>
                        <ActionIcon size="sm" variant="subtle" aria-label={t('tickets.moveDown')} disabled={i === d.fields.length - 1}
                          onClick={() => set({ fields: moveItem(d.fields, i, i + 1) })}>↓</ActionIcon>
                        <Button size="xs" variant="subtle" color="red" onClick={() => set({ fields: d.fields.filter((_, j) => j !== i) })}
                          aria-label={t('tickets.types.removeField', { label: f.label || f.key })}>
                          {t('action.remove')}
                        </Button>
                      </>
                    )}
                  </Group>
                </Group>
              </Stack>
            ))}
            {tried && (problem === 'tickets.types.error.fieldLabel' || problem === 'tickets.types.error.options') && (
              <Text role="caption" c="var(--rr-color-error)">{t(problem)}</Text>
            )}
            {canEdit && d.fields.length < MAX_FIELDS && (
              <div><Button size="xs" variant="subtle" onClick={addField}>{t('tickets.types.addField')}</Button></div>
            )}
          </Stack>

          <Divider />

          <Group gap="sm" grow align="flex-start" wrap="wrap">
            <NumberInput label={t('tickets.order')} value={d.sort_order} onChange={(v) => set({ sort_order: v })} min={0} max={1000} disabled={ro}
              description={t('tickets.orderHint')} />
            <Switch label={t('tickets.active')} description={t('tickets.types.activeHint')} checked={d.active} onChange={(on) => set({ active: on })} disabled={ro} />
          </Group>

          {error && <Alert color="error" role="alert">{error}</Alert>}

          <Group gap="sm" justify="space-between" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }} data-guide="type-save">
            <div>{canEdit && existing && <Button variant="subtle" color="red" onClick={remove}>{t('action.delete')}</Button>}</div>
            <Group gap="sm">
              <Button variant="default" onClick={requestClose}>{canEdit ? t('action.cancel') : t('action.close')}</Button>
              {canEdit && <Button type="submit" loading={busy}>{existing ? t('action.saveChanges') : t('tickets.types.create')}</Button>}
            </Group>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

